import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import React, { useContext, useMemo } from "react"
import { BSFilterContext } from "../../../../../../../../../components/charts/filters/BSFilterContext"
import { RowBarChart } from "../../../../../../../../../components/charts/RowBarChart/RowBarChart"
import { BSMaterialResultDashboard } from "../../../../../../../../../core/dto/beem-shot/BSMaterialResult/BSMaterialResultDashboard"
import { displayDecimal } from "../../../../../../../../../core/services/helper-service"
import { Variant1Context } from "../../../contexts/Variant1Context"
import { Variant2Context } from "../../../contexts/Variant2Context"
import { DifferenceArrowIcon } from "./DifferenceArrowIcon"

interface RowData {
  lot: string
  lotLabel: string
  variantName1: string
  variantName2: string
  buildingName1: string
  buildingName2: string
  difference: number
  totalImpact1: number
  totalImpact2: number
}

export function LotComparaisonTable(): React.JSX.Element {
  const { bsMaterialResultList1, bsBuilding1, variant1 } = useContext(Variant1Context)
  const { bsMaterialResultList2, bsBuilding2, variant2 } = useContext(Variant2Context)
  const { applyLotFilter, applyItemFilter, applyCodeOccurrenceFilter } = useContext(BSFilterContext)

  const filteredBSMaterialResultList1 = useMemo(
    () => applyLotFilter(applyItemFilter(applyCodeOccurrenceFilter(bsMaterialResultList1))),
    [applyLotFilter, applyItemFilter, applyCodeOccurrenceFilter, bsMaterialResultList1]
  )

  const filteredBSMaterialResultList2 = useMemo(
    () => applyLotFilter(applyItemFilter(applyCodeOccurrenceFilter(bsMaterialResultList2))),
    [applyLotFilter, applyItemFilter, applyCodeOccurrenceFilter, bsMaterialResultList2]
  )

  const rowDataGroupedByLot = useMemo(() => {
    const rows = new Map<string, RowData>()
    if (bsBuilding1 && variant1 && variant2) {
      filteredBSMaterialResultList1.forEach((bsMaterialResult: BSMaterialResultDashboard) => {
        const rowData = rows.get(bsMaterialResult.lot)
        if (rowData) {
          rowData.totalImpact1 += bsMaterialResult.re2020CarbonImpactPerSurface
        } else {
          rows.set(bsMaterialResult.lot, {
            lot: bsMaterialResult.lot,
            lotLabel: bsMaterialResult.lotLabel,
            variantName1: variant1.name,
            variantName2: variant2.name,
            buildingName1: bsBuilding1.name,
            buildingName2: "",
            difference: 0,
            totalImpact1: bsMaterialResult.re2020CarbonImpactPerSurface,
            totalImpact2: 0,
          })
        }
      })
    }

    if (bsBuilding2 && variant1 && variant2) {
      filteredBSMaterialResultList2.forEach((bsMaterialResult: BSMaterialResultDashboard) => {
        const rowData = rows.get(bsMaterialResult.lot)
        if (rowData) {
          if (rowData.buildingName2 === "") {
            rowData.buildingName2 = bsBuilding2.name
          }
          rowData.totalImpact2 += bsMaterialResult.re2020CarbonImpactPerSurface
        } else {
          rows.set(bsMaterialResult.lot, {
            lot: bsMaterialResult.lot,
            lotLabel: bsMaterialResult.lotLabel,
            variantName1: variant1.name,
            variantName2: variant2.name,
            buildingName1: "",
            buildingName2: bsBuilding2.name,
            difference: 0,
            totalImpact1: 0,
            totalImpact2: bsMaterialResult.re2020CarbonImpactPerSurface,
          })
        }
      })
    }

    const dataPoints = Array.from(rows.values())
    dataPoints.forEach((dataPoint) => {
      dataPoint.difference = dataPoint.totalImpact1 - dataPoint.totalImpact2
    })

    dataPoints.sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference))
    return dataPoints
  }, [bsBuilding1, variant1, variant2, bsBuilding2, filteredBSMaterialResultList1, filteredBSMaterialResultList2])

  const numbers = useMemo(() => {
    const result: number[] = []
    rowDataGroupedByLot.forEach((rowData) => {
      result.push(rowData.totalImpact1)
      result.push(rowData.totalImpact2)
    })
    return result
  }, [rowDataGroupedByLot])

  const maxValue: number = useMemo(() => Math.max(...numbers), [numbers])
  const minValue: number = useMemo(() => Math.min(...numbers), [numbers])

  return (
    <Paper sx={{ pt: 1 }}>
      <TableContainer sx={{ overflowX: "scroll" }}>
        <Table size="small">
          <TableHead>
            <TableCell>Lot</TableCell>
            <TableCell>Variante</TableCell>
            <TableCell>Bâtiment</TableCell>
            <TableCell>Impact Carbone</TableCell>
            <TableCell>Différence</TableCell>
            <TableCell />
          </TableHead>
          <TableBody>
            {rowDataGroupedByLot.map((rowData) => (
              <TableComparaisonRow key={rowData.lot} rowData={rowData} maxValue={maxValue} minValue={minValue} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

interface IRowData {
  rowData: RowData
  maxValue: number
  minValue: number
}

function TableComparaisonRow({ rowData, maxValue, minValue }: Readonly<IRowData>): React.JSX.Element {
  return (
    <>
      <TableRow>
        <TableCell align="left" rowSpan={2} width="40%">
          {rowData.lotLabel}
        </TableCell>
        <TableCell align="left">{rowData.variantName1}</TableCell>
        <TableCell align="left">{rowData.buildingName1}</TableCell>
        <TableCell align="center">{displayDecimal(rowData.totalImpact1, 1)}</TableCell>
        <TableCell />
        <TableCell align="right">
          <RowBarChart
            maxValue={maxValue}
            minValue={minValue}
            value={rowData.totalImpact1}
            lot={rowData.lot}
            isSelected={false}
            width="200px"
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell align="left">{rowData.variantName2}</TableCell>
        <TableCell align="left">{rowData.buildingName2}</TableCell>
        <TableCell align="center">{displayDecimal(rowData.totalImpact2, 1)}</TableCell>
        <TableCell>
          <DifferenceArrowIcon difference={rowData.difference} />
        </TableCell>
        <TableCell align="right">
          <RowBarChart
            maxValue={maxValue}
            minValue={minValue}
            value={rowData.totalImpact2}
            lot={rowData.lot}
            isSelected={false}
            width="200px"
          />
        </TableCell>
      </TableRow>
    </>
  )
}
